import React, { useState, useEffect } from 'react';
import { getPasswordPolicy, updatePasswordPolicy, getActiveSessions, revokeAllSessions, getLockedAccounts, unlockUser } from '../../services/api';
import { FiShield, FiLock, FiUnlock, FiMonitor, FiAlertTriangle } from 'react-icons/fi';

const SecuritySettings: React.FC = () => {
    const [tab, setTab] = useState<'policy' | 'sessions' | 'locked'>('policy');
    const [policy, setPolicy] = useState<any>(null);
    const [sessions, setSessions] = useState<any[]>([]);
    const [locked, setLocked] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getPasswordPolicy().then(res => setPolicy(res.data));
    }, []);

    useEffect(() => {
        if (tab === 'sessions') getActiveSessions().then(res => setSessions(res.data));
        if (tab === 'locked') getLockedAccounts().then(res => setLocked(res.data));
    }, [tab]);

    const savePolicy = async () => {
        setSaving(true);
        try {
            await updatePasswordPolicy(policy);
            alert('Politique sauvegardee');
        } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
        finally { setSaving(false); }
    };

    const handleRevokeAll = async () => {
        if (window.confirm('Revoquer TOUTES les sessions actives ? Tous les utilisateurs seront deconnectes.')) {
            await revokeAllSessions();
            getActiveSessions().then(res => setSessions(res.data));
        }
    };

    const handleUnlock = async (userId: number) => {
        await unlockUser(userId);
        getLockedAccounts().then(res => setLocked(res.data));
    };

    return (
        <div className="page-security">
            <div className="page-header"><h2><FiShield /> Securite</h2></div>

            <div className="tabs">
                <button className={`tab ${tab === 'policy' ? 'active' : ''}`} onClick={() => setTab('policy')}>Politique MDP</button>
                <button className={`tab ${tab === 'sessions' ? 'active' : ''}`} onClick={() => setTab('sessions')}>Sessions ({sessions.length})</button>
                <button className={`tab ${tab === 'locked' ? 'active' : ''}`} onClick={() => setTab('locked')}>Comptes verrouilles ({locked.length})</button>
            </div>

            {tab === 'policy' && policy && (
                <div className="chart-card">
                    <h3><FiLock /> Politique de mot de passe</h3>
                    <div className="form-row">
                        <div className="form-group"><label>Longueur minimale</label><input type="number" value={policy.min_length} onChange={e => setPolicy({ ...policy, min_length: Number(e.target.value) })} min={4} max={32} /></div>
                        <div className="form-group"><label>Expiration (jours)</label><input type="number" value={policy.max_age_days} onChange={e => setPolicy({ ...policy, max_age_days: Number(e.target.value) })} /></div>
                    </div>
                    <div className="form-row">
                        <div className="form-group"><label>Tentatives max avant verrouillage</label><input type="number" value={policy.max_failed_attempts} onChange={e => setPolicy({ ...policy, max_failed_attempts: Number(e.target.value) })} /></div>
                        <div className="form-group"><label>Duree verrouillage (minutes)</label><input type="number" value={policy.lockout_duration_minutes} onChange={e => setPolicy({ ...policy, lockout_duration_minutes: Number(e.target.value) })} /></div>
                    </div>
                    <div className="form-row">
                        <div className="form-group"><label>Historique (pas de reutilisation des N derniers)</label><input type="number" value={policy.prevent_reuse} onChange={e => setPolicy({ ...policy, prevent_reuse: Number(e.target.value) })} /></div>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                        <label className="checkbox-label"><input type="checkbox" checked={!!policy.require_uppercase} onChange={e => setPolicy({ ...policy, require_uppercase: e.target.checked ? 1 : 0 })} /> Majuscule requise</label>
                        <label className="checkbox-label"><input type="checkbox" checked={!!policy.require_lowercase} onChange={e => setPolicy({ ...policy, require_lowercase: e.target.checked ? 1 : 0 })} /> Minuscule requise</label>
                        <label className="checkbox-label"><input type="checkbox" checked={!!policy.require_digit} onChange={e => setPolicy({ ...policy, require_digit: e.target.checked ? 1 : 0 })} /> Chiffre requis</label>
                        <label className="checkbox-label"><input type="checkbox" checked={!!policy.require_special} onChange={e => setPolicy({ ...policy, require_special: e.target.checked ? 1 : 0 })} /> Caractere special requis</label>
                    </div>
                    <button className="btn btn-primary" onClick={savePolicy} disabled={saving}>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</button>
                </div>
            )}

            {tab === 'sessions' && (
                <>
                    <div className="filters-bar">
                        <button className="btn btn-secondary" onClick={handleRevokeAll}><FiAlertTriangle /> Revoquer toutes les sessions</button>
                    </div>
                    <div className="table-container">
                        <table className="data-table">
                            <thead><tr><th>Utilisateur</th><th>IP</th><th>Appareil</th><th>Expire</th><th>Cree le</th></tr></thead>
                            <tbody>
                                {sessions.map(s => (
                                    <tr key={s.id}>
                                        <td><strong>{s.user_nom || s.user_id}</strong></td>
                                        <td>{s.ip_address}</td>
                                        <td><small>{(s.user_agent || '').substring(0, 60)}</small></td>
                                        <td>{new Date(s.expires_at).toLocaleString('fr')}</td>
                                        <td>{new Date(s.created_at).toLocaleString('fr')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {tab === 'locked' && (
                <div className="table-container">
                    <table className="data-table">
                        <thead><tr><th>Utilisateur</th><th>Email</th><th>Tentatives</th><th>Verrouille jusqu'a</th><th>Actions</th></tr></thead>
                        <tbody>
                            {locked.length === 0 ? <tr><td colSpan={5} className="text-muted" style={{ textAlign: 'center', padding: 20 }}>Aucun compte verrouille</td></tr> :
                                locked.map(u => (
                                    <tr key={u.id}>
                                        <td><strong>{u.prenom} {u.nom}</strong></td>
                                        <td>{u.email}</td>
                                        <td>{u.failed_login_attempts}</td>
                                        <td>{new Date(u.locked_until).toLocaleString('fr')}</td>
                                        <td><button className="btn btn-sm btn-primary" onClick={() => handleUnlock(u.id)}><FiUnlock /> Debloquer</button></td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SecuritySettings;
